// Type definitions for heatmap-initiated bin-to-bin moves (drag&drop).
// Matches Shared.DTOs.HeatmapMove.HeatmapMoveDTOs in the backend.

export enum HeatmapMoveReason {
    Reorganization = 0,
    PickOptimization = 1,
    Consolidation = 2,
    Replenishment = 3,
    AbcReclassification = 4,
    Misplaced = 5,
    Rebuild = 6,
    Other = 99
}

export enum HeatmapMovePriority {
    Low = 1,
    Normal = 5,
    High = 7,
    Urgent = 9
}

export enum MoveValidationSeverity {
    Warning = 1,
    Error = 2
}

export interface MoveValidationMessage {
    code: string;
    message: string;
    severity: MoveValidationSeverity;
}

export interface MoveCandidateItem {
    itemNo: string;
    description1?: string;
    itemUnitCode?: string;
    totalQuantity: number;
    reservedQuantity: number;
    /** Already earmarked as FromBin in active plan steps — subtracted from availableQuantity. */
    plannedQuantity: number;
    availableQuantity: number;
    batchNo?: string;
    expiryDate?: string;
    serialNo?: string;
    itemType: number;
}

export interface DestinationBinInfo {
    binCode: string;
    binTypeName?: string;
    binCategoryNo?: number;
    binGrpNo?: number;
    reservationAllowed: boolean;
    pickableYesNo: boolean;
    sharedBinYesNo: boolean;
    currentQuantity: number;
    maxQuantity?: number;
    availableSpace?: number;
    occupyingItemNo?: string;
    occupyingBatchNo?: string;
}

export interface PlanMoveRequest {
    warehouseID: string;
    fromBinCode: string;
    toBinCode: string;
    itemNo?: string;
}

export interface PlanMoveResponse {
    success: boolean;
    errorMessage?: string;
    canProceed: boolean;
    items: MoveCandidateItem[];
    destinationInfo?: DestinationBinInfo;
    errors: MoveValidationMessage[];
    warnings: MoveValidationMessage[];
}

export interface ExecuteMoveRequest {
    warehouseID: string;
    fromBinCode: string;
    toBinCode: string;
    itemNo: string;
    quantity: number;
    reason: HeatmapMoveReason;
    priority: HeatmapMovePriority;
    comment?: string;
}

export interface ExecuteMoveResponse {
    success: boolean;
    errorMessage?: string;
    stcOrderNo?: number;
    relID?: string;
    reservationId?: number;
}

/**
 * UI-friendly labels for the reason enum (Norwegian).
 * Keep in sync with backend HeatmapMoveReason.
 */
export const HEATMAP_MOVE_REASON_LABELS: Record<HeatmapMoveReason, string> = {
    [HeatmapMoveReason.Reorganization]: 'Omplassering',
    [HeatmapMoveReason.PickOptimization]: 'Plukkoptimalisering',
    [HeatmapMoveReason.Consolidation]: 'Konsolidering',
    [HeatmapMoveReason.Replenishment]: 'Påfyll',
    [HeatmapMoveReason.AbcReclassification]: 'ABC-omstokking',
    [HeatmapMoveReason.Misplaced]: 'Feilplassert',
    [HeatmapMoveReason.Rebuild]: 'Ombygging',
    [HeatmapMoveReason.Other]: 'Annet'
};

export const HEATMAP_MOVE_PRIORITY_LABELS: Record<HeatmapMovePriority, string> = {
    [HeatmapMovePriority.Low]: 'Lav',
    [HeatmapMovePriority.Normal]: 'Normal',
    [HeatmapMovePriority.High]: 'Høy',
    [HeatmapMovePriority.Urgent]: 'Haster'
};

// ====================================================================
// BATCH MOVE — Fase 2A: multi-source, one destination
// ====================================================================

export interface BatchMoveSource {
    fromBinCode: string;
    itemNo: string;
    quantity: number;
    /** Optional per-line destination — overrides the request's toBinCode for this line. */
    toBinCodeOverride?: string;
}

export interface BatchMoveSourceCandidates {
    fromBinCode: string;
    items: MoveCandidateItem[];
}

export interface PlanBatchMoveRequest {
    warehouseID: string;
    toBinCode: string;
    fromBinCodes: string[];
}

export interface PlanBatchMoveResponse {
    success: boolean;
    errorMessage?: string;
    canProceed: boolean;
    sources: BatchMoveSourceCandidates[];
    destinationInfo?: DestinationBinInfo;
    errors: MoveValidationMessage[];
    warnings: MoveValidationMessage[];
}

export interface ExecuteBatchMoveRequest {
    warehouseID: string;
    /** Shared destination. Can be omitted if every source uses toBinCodeOverride. */
    toBinCode?: string;
    sources: BatchMoveSource[];
    reason: HeatmapMoveReason;
    priority: HeatmapMovePriority;
    comment?: string;
}

export interface BatchMoveLineResult {
    fromBinCode: string;
    itemNo: string;
    quantity: number;
    toBinCode: string;
    success: boolean;
    errorMessage?: string;
    relID?: string;
    reservationId?: number;
    stcLineNo?: number;
}

// ====================================================================
// SUGGEST DESTINATIONS — Fase 2B
// ====================================================================

export interface SuggestDestinationsRequest {
    warehouseID: string;
    fromBinCode: string;
    itemNo?: string;
}

export interface SuggestedBin {
    binCode: string;
    quantity: number;
    reason?: string;
    priority: number;
}

export interface ItemSuggestion {
    fromBinCode: string;
    itemNo: string;
    description1?: string;
    availableQuantity: number;
    destinations: SuggestedBin[];
    errorMessage?: string;
}

export interface SuggestDestinationsResponse {
    success: boolean;
    errorMessage?: string;
    suggestions: ItemSuggestion[];
}

// ====================================================================
// Active-move bins — pending/in-progress moves shown in edit-mode
// ====================================================================

/**
 * One pending/in-progress move row affecting a bin. Multiple entries may
 * share the same bin (e.g. source of one order + destination in a draft plan).
 */
export interface ActiveMoveBin {
    binCode: string;
    role: string;
    itemNo?: string;
    quantity: number;
    relatedInfo?: string;
    description?: string;
}

export interface ExecuteBatchMoveResponse {
    success: boolean;
    errorMessage?: string;
    stcOrderNo?: number;
    results: BatchMoveLineResult[];
    successCount: number;
    failedCount: number;
}
